import type { SxProps, Theme } from "@mui/material";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import type { ChangeEventHandler } from "react";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface ReactHookTextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  control: Control<TFieldValues>;
  defaultValue?: string;
  label: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  required?: boolean;
  requiredAsterisk?: boolean;
  sx?: SxProps<Theme>;
  multiline?: boolean;
  minRows?: number;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  fullWidth?: boolean;
  maxRows?: number;
  autoComplete?: string;
  textFieldProps?: TextFieldProps;
  maxLength?: number;
}
export function ReactHookTextField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  rules = {},
  required = false,
  requiredAsterisk = true,
  multiline = false,
  minRows = 1,
  maxRows,
  disabled = false,
  sx = {},
  onChange,
  fullWidth = false,
  autoComplete,
  textFieldProps = {},
  control,
  maxLength,
}: ReactHookTextFieldProps<TFieldValues, TName>) {
  const { t } = useTranslation();
  const labelPrefix = required && requiredAsterisk ? "* " : "";
  const actualLabel = `${labelPrefix}${label}`;
  return (
    <Controller
      name={name}
      rules={{
        required: required
          ? t("Next:Core:FieldRequiredValidationMessage")
          : false,
        ...rules,
      }}
      control={control}
      render={({
        field: { ref: inputRef, ...field },
        fieldState: { error },
      }) => (
        <TextField
          {...field}
          {...textFieldProps}
          slotProps={{
            ...textFieldProps.slotProps,
            htmlInput: { ...textFieldProps?.slotProps?.htmlInput, maxLength },
          }}
          inputRef={inputRef}
          autoComplete={autoComplete}
          onChange={onChange || field.onChange}
          label={actualLabel}
          error={Boolean(error)}
          disabled={disabled}
          minRows={minRows}
          maxRows={maxRows}
          sx={sx}
          helperText={Boolean(error) && error?.message}
          multiline={multiline}
          fullWidth={fullWidth}
          data-testid={`textfield-${name}`}
        />
      )}
    />
  );
}
